import { SearchInput } from '@/components/ui/search-input'
import { FilterSelect, FilterMultiSelect } from '@/components/ui/filters'
import type { EndDatePreset } from '@/components/ui/date-picker'
import { FormField } from '@/components/ui/form-field'
import DepartmentSelect from '@/components/Form/Shared/DepartmentSelect'
import PositionSelect from '@/components/Form/Shared/PositionSelect'
import { ReactNode, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useTableSettings } from '@/hooks/useTableSettings'
import { Control } from 'react-hook-form'
import { AsyncFilterSelect } from '../../filters/async-filter-select'

// ============================================================================
// Filter Item Types
// ============================================================================

interface BaseFilterItem {
    /** Unique name for the filter (also used as form field name) */
    name: string
    /** Columns to span on md+ (1-6), default 1 */
    cols?: 1 | 2 | 3 | 4 | 5 | 6
    /** Enable/disable this filter item, default true */
    enabled?: boolean
    /** Toggle key for visibility control (linked to DataTable settings) */
    toggleKey?: string
    /** Default visibility when toggle doesn't exist, default true */
    defaultVisible?: boolean
    /** Custom label (auto-generated from name if not provided) */
    label?: string
    /** Custom placeholder (auto-generated from name if not provided) */
    placeholder?: string
    /** Additional className */
    className?: string
}

interface SelectFilterItem extends BaseFilterItem {
    type: 'select'
    options: Array<{ label: string; value: string | number }>
    /** Custom change handler (overrides default setFilter) */
    onChange?: (value: any) => void
}

interface MultiSelectFilterItem extends BaseFilterItem {
    type: 'multi-select'
    options: Array<{ label: string; value: string | number }>
    /** Custom change handler */
    onChange?: (value: any[]) => void
}

interface AsyncSelectFilterItem extends BaseFilterItem {
    type: 'async-select'
    /** Async options fetcher - returns options array or page object with hasMore */
    asyncOptions: (
        query: string,
        page?: number
    ) => Promise<{ options: any[]; hasMore?: boolean } | any[]>
    /** Fetch default options by IDs */
    getDefaultOptions?: (ids: any[]) => Promise<any[]>
    /** Query key for caching */
    queryKey?: string
    /** Enable multiple selection */
    multiple?: boolean
    /** Custom change handler */
    onChange?: (value: any) => void
}

interface DateRangeFilterItem extends BaseFilterItem {
    type: 'daterangepicker'
    /** Field names for [from, to] dates */
    names: [string, string]
    /** Show preset buttons */
    showPresets?: boolean
    /** Number of months to display */
    numberOfMonths?: 1 | 2
    /** Minimum selectable date */
    minDate?: Date
    /** Maximum selectable date */
    maxDate?: Date
    /** Quick end-date presets */
    endDatePresets?: EndDatePreset[]
    /** Show clear button to reset the date range (default: false) */
    clearable?: boolean
    /** Custom change handler - receives [fromDate, toDate] as YYYY-MM-DD strings */
    onChange?: (value: [string, string]) => void
}

interface DepartmentFilterItem extends BaseFilterItem {
    type: 'department'
    /** Custom change handler */
    onChange?: (value: number) => void
}

interface PositionFilterItem extends BaseFilterItem {
    type: 'position'
    /** Department ID to filter positions */
    departmentId?: number
    /** Custom change handler */
    onChange?: (value: number) => void
}

interface CheckboxFilterItem extends BaseFilterItem {
    type: 'checkbox'
    /** Description text beside checkbox */
    description?: string
    /** Show bordered box matching filter input height (h-9) */
    bordered?: boolean
    /** Custom change handler */
    onChange?: (value: boolean) => void
}

interface SwitchFilterItem extends BaseFilterItem {
    type: 'switch'
    /** Description text beside switch */
    description?: string
    /** Custom change handler */
    onChange?: (value: boolean) => void
}

interface CustomFilterItem extends BaseFilterItem {
    type: 'custom'
    /** Custom component to render */
    component: ReactNode
}

export type FilterItemConfig =
    | SelectFilterItem
    | MultiSelectFilterItem
    | AsyncSelectFilterItem
    | DateRangeFilterItem
    | DepartmentFilterItem
    | PositionFilterItem
    | CheckboxFilterItem
    | SwitchFilterItem
    | CustomFilterItem

// ============================================================================
// FilterBar Component
// ============================================================================

interface FilterBarProps<TFilters extends Record<string, any> = any> {
    /** Declarative filter items config */
    items?: FilterItemConfig[]
    /** Storage key for settings (shared with DataTableCustomized) */
    storageKey?: string
    /** React Hook Form control */
    control?: Control<TFilters>
    /** Filter setter from useUrlSearchParams */
    setFilter?: (key: keyof TFilters, value: any) => void
    /** Batch filter setter from useUrlSearchParams */
    setFilters?: (updates: Partial<TFilters>) => void
    /** Props from useUrlSearchParams().searchInputProps */
    searchInputProps?: {
        value: string
        onChange: (value: string) => void
    }
    /** Custom search placeholder */
    searchPlaceholder?: string
    /** Hide search input */
    hideSearch?: boolean
    /** Filter components (legacy children support) */
    children?: ReactNode
    /** Additional className */
    className?: string
    /** Tour prefix for data-tour attributes, default 'filter' */
    tourPrefix?: string
}

/**
 * FilterBar - Config-driven responsive filter layout
 *
 * Supports multiple filter types:
 * - `select`: FilterSelect component
 * - `multi-select`: FilterMultiSelect component
 * - `async-select`: AsyncFilterSelect component
 * - `daterangepicker`: DateRangePicker with two separate field names (same as FormField)
 * - `department`: DepartmentSelect component
 * - `position`: PositionSelect component
 * - `checkbox`: Checkbox with label (bordered by default)
 * - `switch`: Switch toggle with label
 * - `custom`: Any custom component
 *
 * @example
 * <FilterBar
 *   storageKey="dayoff-list"
 *   control={control}
 *   setFilter={setFilter}
 *   items={[
 *     { name: 'status', type: 'select', options: STATUS_OPTIONS, toggleKey: 'showStatusFilter' },
 *     { name: 'department_id', type: 'department', cols: 2, enabled: isHRM },
 *     { name: 'date_range', type: 'daterangepicker' },
 *   ]}
 * />
 */
export function FilterBar<TFilters extends Record<string, any> = any>({
    items,
    storageKey,
    control,
    setFilter,
    setFilters,
    searchInputProps,
    searchPlaceholder,
    hideSearch = false,
    children,
    className = '',
    tourPrefix = 'filter',
}: Readonly<FilterBarProps<TFilters>>) {
    const { t } = useTranslation('data-table')
    const { isToggleEnabled } = useTableSettings(storageKey)

    // Auto-generate label from name
    const getLabel = (item: FilterItemConfig) => {
        if (item.label) return item.label
        return t(item.name, { defaultValue: item.name.replace(/_/g, ' ') })
    }

    // Auto-generate placeholder from name
    const getPlaceholder = (item: FilterItemConfig) => {
        if (item.placeholder) return item.placeholder
        return t(`select_${item.name}`, {
            defaultValue: `Select ${item.name.replace(/_/g, ' ')}`,
        })
    }

    // Default change handler
    const getOnChange = (item: FilterItemConfig) => {
        if ('onChange' in item && item.onChange) return item.onChange
        if (setFilter)
            return (val: any) => setFilter(item.name as keyof TFilters, val)
        return undefined
    }

    // Apply daterangepicker value to filters (handles legacy array form + split gte/lte form)
    const applyDateRangeToFilters = (
        item: DateRangeFilterItem,
        val: [string, string]
    ) => {
        // Legacy pages keep range as array on primary field (name === names[0])
        if (item.name === item.names[0]) {
            if (!setFilter) return
            const arrayVal = val[0] ? [val[0], val[1]] : undefined
            setFilter(item.names[0] as keyof TFilters, arrayVal as any)
            return
        }

        // New pages (e.g. gte/lte) split to 2 scalar fields
        if (setFilters) {
            setFilters({
                [item.names[0]]: (val[0] ||
                    undefined) as unknown as TFilters[keyof TFilters],
                [item.names[1]]: (val[1] ||
                    undefined) as unknown as TFilters[keyof TFilters],
            } as Partial<TFilters>)
        } else if (setFilter) {
            setFilter(
                item.names[0] as keyof TFilters,
                (val[0] || undefined) as unknown as any
            )
            setFilter(
                item.names[1] as keyof TFilters,
                (val[1] || undefined) as unknown as any
            )
        }
    }

    // Memoize filtered items based on enabled and toggle visibility
    const visibleItems = useMemo(() => {
        if (!items) return []

        return items.filter((item) => {
            if (item.enabled === false) return false
            if (item.toggleKey && storageKey) {
                return isToggleEnabled(
                    item.toggleKey,
                    item.defaultVisible ?? true
                )
            }
            return true
        })
    }, [items, storageKey, isToggleEnabled])

    // Generate col-span class
    const getColSpan = (cols: number = 1) => {
        if (cols === 1) return 'col-span-1'
        return `col-span-1 md:col-span-${cols}`
    }

    // Render filter component based on type
    const renderFilterComponent = (item: FilterItemConfig) => {
        // Skip rendering if control is required but not provided
        if (!control && item.type !== 'custom') {
            return null
        }

        const commonProps = {
            control: control!,
            name: item.name as any,
            label: getLabel(item),
            placeholder: getPlaceholder(item),
            onChangeAfterHookForm: getOnChange(item),
        }

        switch (item.type) {
            case 'select':
                return <FilterSelect {...commonProps} options={item.options} />

            case 'multi-select':
                return (
                    <FilterMultiSelect
                        {...commonProps}
                        options={item.options}
                        onChangeAfterHookForm={getOnChange(item) as any}
                    />
                )

            case 'async-select':
                return (
                    <AsyncFilterSelect<TFilters>
                        {...commonProps}
                        asyncOptions={item.asyncOptions as any}
                        getDefaultOptions={item.getDefaultOptions}
                        queryKey={item.queryKey}
                        multiple={item.multiple}
                    />
                )

            case 'daterangepicker': {
                const onChange = item.onChange
                const handleDateRangeChange = (val: [string, string]) => {
                    if (onChange) {
                        onChange(val)
                    } else if (setFilter || setFilters) {
                        applyDateRangeToFilters(item, val)
                    }
                }
                return (
                    <FormField
                        type="daterangepicker"
                        control={control!}
                        name={item.names as any}
                        label={getLabel(item)}
                        placeholder={getPlaceholder(item)}
                        showPresets={item.showPresets}
                        numberOfMonths={item.numberOfMonths}
                        minDate={item.minDate}
                        maxDate={item.maxDate}
                        endDatePresets={item.endDatePresets}
                        clearable={item.clearable}
                        onValueChange={handleDateRangeChange}
                    />
                )
            }

            case 'department':
                return (
                    <DepartmentSelect
                        control={control!}
                        name={item.name as any}
                        onChangeAfterHookForm={getOnChange(item) as any}
                    />
                )

            case 'position':
                return (
                    <PositionSelect
                        control={control!}
                        name={item.name as any}
                        departmentId={item.departmentId!}
                        onChangeAfterHookForm={getOnChange(item) as any}
                    />
                )

            case 'checkbox':
                return (
                    <FormField
                        type="checkbox"
                        control={control!}
                        name={item.name as any}
                        label={getLabel(item)}
                        bordered={item.bordered !== false}
                        onValueChange={getOnChange(item)}
                    />
                )

            case 'switch':
                return (
                    <FormField
                        type="switch"
                        control={control!}
                        name={item.name as any}
                        label={getLabel(item)}
                        bordered
                        onValueChange={getOnChange(item)}
                    />
                )

            case 'custom':
                return item.component

            default:
                return null
        }
    }

    return (
        <div className={`gap-2 relative overflow-visible ${className}`}>
            {/* Grid filters - 1 col on xs, 6 cols on md+ */}
            <div
                className={`grid grid-cols-1 md:grid-cols-6 gap-2 relative z-40 overflow-visible ${visibleItems?.length > 0 || !!children ? 'mb-2' : ''}`}
            >
                {/* Config-driven items */}
                {visibleItems.map((item) => (
                    <div
                        key={item.name}
                        data-tour={`${tourPrefix}-${item.name}`}
                        className={`${getColSpan(item.cols)} overflow-visible ${item.className || ''}`}
                    >
                        {renderFilterComponent(item)}
                    </div>
                ))}

                {/* Legacy children support */}
                {children}
            </div>

            {/* Actions row: Search - lower z-index */}
            {!hideSearch && searchInputProps && (
                <div className="flex items-center justify-end gap-2 relative z-10">
                    <SearchInput
                        {...searchInputProps}
                        placeholder={searchPlaceholder}
                    />
                </div>
            )}
        </div>
    )
}

// ============================================================================
// Legacy FilterBar.Item (for backwards compatibility)
// ============================================================================

interface FilterBarItemProps {
    children: ReactNode
    cols?: 1 | 2 | 3 | 4 | 5 | 6 | 9 | 12
    /** @deprecated Use cols prop instead */
    size?: 'sm' | 'md' | 'lg' | 'xl' | 'full'
    /** @deprecated Use cols prop instead */
    width?: string
    className?: string
    toggleKey?: string
    storageKey?: string
    defaultVisible?: boolean
    tourName?: string
}

const sizeToCols: Record<string, 1 | 2 | 3 | 4 | 5 | 6 | 9 | 12> = {
    sm: 1,
    md: 1,
    lg: 2,
    xl: 3,
    full: 12,
}

function FilterBarItem({
    children,
    cols,
    size,
    width,
    className = '',
    toggleKey,
    storageKey,
    defaultVisible = true,
    tourName,
}: Readonly<FilterBarItemProps>) {
    const { isToggleEnabled } = useTableSettings(storageKey)

    if (toggleKey && storageKey) {
        const isVisible = isToggleEnabled(toggleKey, defaultVisible)
        if (!isVisible) return null
    }

    if (width) {
        return (
            <div className={`${width} ${className}`} data-tour={tourName}>
                {children}
            </div>
        )
    }

    const effectiveCols = cols ?? (size ? sizeToCols[size] : 1)
    const colSpan =
        effectiveCols === 1
            ? 'col-span-1'
            : `col-span-1 md:col-span-${effectiveCols}`

    return (
        <div
            className={`${colSpan} overflow-visible ${className}`}
            data-tour={tourName}
        >
            {children}
        </div>
    )
}

FilterBar.Item = FilterBarItem
